import { z, ZodType } from 'zod';

import { ReportColumn, ReportSortBy, ReportTransactionRole, TransactionType } from './common';
import { ReportFormat } from './report';
import { DateStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint, paginateResponseSchema, stringBooleanSchema } from '../common';
import { PaymentMethods } from '../payment-method/public';

export const FinancialReportColumn = [
  ReportColumn.EXTERNAL_REFERENCE_ID,
  ReportColumn.BRAND_NAME,
  ReportColumn.CURRENCY,
  ReportColumn.EXPERIENCE_DATE,
  ReportColumn.TRANSACTION_DATE,
  ReportColumn.TRANSACTION_MONTH,
  ReportColumn.FIRST_NAME,
  ReportColumn.LAST_NAME,
  ReportColumn.CONFIRMATION_CODE,
  ReportColumn.PAYMENT_TYPE,
  ReportColumn.PAYMENT_METHOD,
  ReportColumn.ACCOUNT,
  ReportColumn.DESCRIPTION,
  ReportColumn.DURATION,
  ReportColumn.HOST,
  ReportColumn.TOTAL,
  ReportColumn.DISCOUNT_AMOUNT,
  ReportColumn.SUBTOTAL,
  ReportColumn.GROSS_REVENUE,
  ReportColumn.NET_REVENUE,
  ReportColumn.TAXES,
  ReportColumn.TOTAL_PAID,
  ReportColumn.FX_ADJUSTMENT,
  ReportColumn.HOST_PAYOUT,
  ReportColumn.PROMOTER_PAYOUT,
  ReportColumn.CREDIT_CARD,
  ReportColumn.STRIPE_FX_FEE,
  ReportColumn.WAY_FEE,
  ReportColumn.TRANSACTION_ROLE,
  ReportColumn.PARTNER_NAME,
  ReportColumn.PARTNER_PAYOUT,
  ReportColumn.NET_DEPOSITED,
  ReportColumn.QUANTITY,
  ReportColumn.DISCOUNT_CODE,
  ReportColumn.DATE_DEPOSITED,
  ReportColumn.NOTES,
  ReportColumn.INTERNAL_NOTES,
  ReportColumn.RESERVATION_ID,
  ReportColumn.CART_ID,
  ReportColumn.CREATED_BY,
  ReportColumn.CATEGORY,
  ReportColumn.LISTING_STATUS,
  ReportColumn.BOOKING_URL,
  ReportColumn.PROMOTER_SOURCE,
  ReportColumn.APPROVED_PROMOTERS,
] as const satisfies ReadonlyArray<ReportColumn>;
export type FinancialReportColumnType = (typeof FinancialReportColumn)[number];

export const financialColumnsMap = {
  [ReportColumn.EXTERNAL_REFERENCE_ID]: 'External Reference ID',
  [ReportColumn.BRAND_NAME]: 'Brand Name',
  [ReportColumn.CURRENCY]: 'Currency',
  [ReportColumn.EXPERIENCE_DATE]: 'Experience Date',
  [ReportColumn.TRANSACTION_DATE]: 'Transaction Date UTC',
  [ReportColumn.TRANSACTION_MONTH]: 'Transaction Month UTC',
  [ReportColumn.FIRST_NAME]: 'First Name',
  [ReportColumn.LAST_NAME]: 'Last Name',
  [ReportColumn.CONFIRMATION_CODE]: 'Confirmation Code',
  [ReportColumn.PAYMENT_TYPE]: 'Type',
  [ReportColumn.PAYMENT_METHOD]: 'Payment Method',
  [ReportColumn.ACCOUNT]: 'Account',
  [ReportColumn.DESCRIPTION]: 'Description',
  [ReportColumn.DURATION]: 'Duration',
  [ReportColumn.HOST]: 'Host',
  [ReportColumn.TOTAL]: 'Total',
  [ReportColumn.DISCOUNT_AMOUNT]: 'Discount Amount',
  [ReportColumn.SUBTOTAL]: 'Subtotal',
  [ReportColumn.GROSS_REVENUE]: 'Gross Revenue',
  [ReportColumn.NET_REVENUE]: 'Net Revenue',
  [ReportColumn.TAXES]: 'Taxes',
  [ReportColumn.TOTAL_PAID]: 'Total Paid',
  [ReportColumn.FX_ADJUSTMENT]: 'FX Adjustment',
  [ReportColumn.HOST_PAYOUT]: 'Host Payout',
  [ReportColumn.PROMOTER_PAYOUT]: 'Promoter Payout',
  [ReportColumn.CREDIT_CARD]: 'Credit Card',
  [ReportColumn.STRIPE_FX_FEE]: 'Stripe FX Fee',
  [ReportColumn.WAY_FEE]: 'Way Fee',
  [ReportColumn.TRANSACTION_ROLE]: 'Transaction Role',
  [ReportColumn.PARTNER_NAME]: 'Partner Name',
  [ReportColumn.PARTNER_PAYOUT]: 'Partner Payout',
  [ReportColumn.NET_DEPOSITED]: 'Net Deposited',
  [ReportColumn.QUANTITY]: 'Quantity',
  [ReportColumn.DISCOUNT_CODE]: 'Discount Code',
  [ReportColumn.DATE_DEPOSITED]: 'Date Deposited',
  [ReportColumn.NOTES]: 'Guest Notes',
  [ReportColumn.INTERNAL_NOTES]: 'Internal Notes',
  [ReportColumn.RESERVATION_ID]: 'Reservation ID',
  [ReportColumn.CART_ID]: 'Cart ID',
  [ReportColumn.CREATED_BY]: 'Created By',
  [ReportColumn.CATEGORY]: 'Category',
  [ReportColumn.LISTING_STATUS]: 'Listing Status',
  [ReportColumn.BOOKING_URL]: 'Booking URL',
  [ReportColumn.PROMOTER_SOURCE]: 'Promoter Source',
  [ReportColumn.APPROVED_PROMOTERS]: 'Approved Promoters',
} as const satisfies Record<FinancialReportColumnType, string>;

export const FinancialReportSortBy = [
  ReportSortBy.START_DATE_TIME,
  ReportSortBy.TRANSACTION_DATE,
] as const satisfies ReadonlyArray<ReportSortBy>;

export const financialReportInfoSchema = {
  filters: {
    from: 'datestring',
    to: 'datestring',
    experienceId: 'uuid',
    resourceGroupCollectionId: 'uuid',
    eventId: 'uuid',
    searchParams: 'string',
    transactionType: Object.values(TransactionType),
    showNegativeDiscountValues: 'boolean',
    dateDeposited: 'datestring',
  },
  sortBy: FinancialReportSortBy,
  columns: Object.entries(financialColumnsMap).map(([key, value]) => ({
    label: value,
    field: key,
  })),
} as const;

const financialReportFiltersSchema = z.object({
  from: z.union([DateStringSchema, z.string().datetime()]).optional(),
  to: z.union([DateStringSchema, z.string().datetime()]).optional(),
  experienceId: z.string().optional().nullable(),
  resourceGroupCollectionId: z.string().uuid().optional().nullable(),
  eventId: z.string().optional().nullable(),
  searchParams: z.string().optional().nullable(),
  transactionType: z.nativeEnum(TransactionType).optional().nullable(),
  showNegativeDiscountValues: z.boolean().optional().nullable(),
  dateDeposited: DateStringSchema.optional().nullable(),
} satisfies Record<keyof typeof financialReportInfoSchema.filters, z.ZodType>);

export type FinancialReportFilters = z.infer<typeof financialReportFiltersSchema>;

const BaseFinancialReportRequestSchema = z.object({
  params: financialReportFiltersSchema,
  sortBy: z.enum(FinancialReportSortBy),
  columns: z.array(z.enum(FinancialReportColumn)),
  format: z.enum([ReportFormat.CSV, ReportFormat.PDF]).optional(),
});
export const BrandFinancialReportRequestSchema = BaseFinancialReportRequestSchema.extend({ brandId: z.string().uuid() });
export const OrganizationFinancialReportRequestSchema = BaseFinancialReportRequestSchema.extend({
  organizationId: z.string().uuid(),
});
export const FinancialReportRequestSchema = z.union([
  BrandFinancialReportRequestSchema,
  OrganizationFinancialReportRequestSchema,
]);
export type FinancialReportRequest = z.infer<typeof FinancialReportRequestSchema>;

const PreviewFinancialRequestSchema = financialReportFiltersSchema
  .omit({
    searchParams: true,
    showNegativeDiscountValues: true,
  })
  .extend({
    limit: z.coerce.number().optional(),
    page: z.coerce.number().optional(),
    sortBy: z.enum(FinancialReportSortBy).default(ReportSortBy.START_DATE_TIME),
    organizationId: z.string().uuid().optional(),
    q: z.string().optional(),
    confirmationCode: z.string().optional(),
    showNegativeDiscountValues: stringBooleanSchema.optional(),
  });

export const FinancialReportWithHeaderSchema = z.object({
  'External Reference ID': z.string().optional(),
  'Brand Name': z.string().optional(),
  Currency: z.string().optional(),
  'Experience Date': z.string().optional(),
  'Transaction Date UTC': z.string().optional(),
  'Transaction Month UTC': z.string().optional(),
  'First Name': z.string().optional(),
  'Last Name': z.string().optional(),
  'Confirmation Code': z.string().optional(),
  Type: z.string().optional(),
  'Payment Method': z.string().optional(),
  Account: z.string().optional(),
  Description: z.string().optional(),
  Duration: z.string().optional(),
  Host: z.string().optional(),
  Total: z.string().optional(),
  'Discount Amount': z.string().optional(),
  Subtotal: z.string().optional(),
  'Gross Revenue': z.string().optional(),
  'Net Revenue': z.string().optional(),
  'Total Paid': z.string().optional(),
  'FX Adjustment': z.string().optional(),
  'Host Payout': z.string().optional(),
  'Promoter Payout': z.string().optional(),
  'Credit Card': z.string().optional(),
  'Stripe FX Fee': z.string().optional(),
  'Way Fee': z.string().optional(),
  'Transaction Role': z.string().optional(),
  'Partner Name': z.string().optional(),
  'Partner Payout': z.string().optional(),
  'Net Deposited': z.string().optional(),
  Quantity: z.string().optional(),
  'Discount Code': z.string().optional(),
  'Date Deposited': z.string().optional(),
  'Guest Notes': z.string().optional(),
  'Internal Notes': z.string().optional(),
  'Reservation ID': z.string().optional(),
  'Cart ID': z.string().optional(),
  'Created By': z.string().optional(),
  Category: z.string().optional(),
  'Listing Status': z.string().optional(),
  'Booking URL': z.string().optional(),
  'Promoter Source': z.string().optional(),
  'Approved Promoters': z.string().optional(),
} satisfies Record<(typeof financialColumnsMap)[Exclude<keyof typeof financialColumnsMap, ReportColumn.TAXES>], z.ZodType>);
export type FinancialReportWithHeader = z.input<typeof FinancialReportWithHeaderSchema>;

export const webhookFinancialReportV1ItemSchema = z.object({
  [ReportColumn.EXTERNAL_REFERENCE_ID]: z.string().nullable(),
  [ReportColumn.BRAND_NAME]: z.string(),
  brandId: z.string(),
  experienceId: z.string(),
  bookingId: z.string().nullable(),
  bookingSourceUrl: z.string().url().nullable(),
  loyaltyMemberId: z.string().nullable(),
  loyaltyPoints: z.number().nullable(),
  externalAnalyticsUserId: z.string().nullable(),
  loyaltyTransactionId: z.string().nullable(),
  [ReportColumn.CURRENCY]: z.string(),
  [ReportColumn.EXPERIENCE_DATE]: DateStringSchema,
  [ReportColumn.TRANSACTION_DATE]: z.string().datetime(),
  [ReportColumn.TRANSACTION_MONTH]: z.string(),
  [ReportColumn.FIRST_NAME]: z.string().nullable(),
  [ReportColumn.LAST_NAME]: z.string().nullable(),
  [ReportColumn.CONFIRMATION_CODE]: z.string().nullable(),
  [ReportColumn.PAYMENT_TYPE]: z.string(),
  [ReportColumn.PAYMENT_METHOD]: z.nativeEnum(PaymentMethods),
  [ReportColumn.ACCOUNT]: z.string().nullable(),
  [ReportColumn.DESCRIPTION]: z.string(),
  [ReportColumn.DURATION]: z.string(),
  [ReportColumn.HOST]: z.string().nullable(),
  [ReportColumn.TOTAL]: z.number().nullable(),
  [ReportColumn.DISCOUNT_AMOUNT]: z.number().nullable(),
  [ReportColumn.SUBTOTAL]: z.number().nullable(),
  [ReportColumn.GROSS_REVENUE]: z.number().nullable(),
  [ReportColumn.NET_REVENUE]: z.number().nullable(),
  [ReportColumn.TAXES]: z.record(z.number()),
  [ReportColumn.TOTAL_PAID]: z.number().nullable(),
  [ReportColumn.FX_ADJUSTMENT]: z.number().nullable().optional(),
  [ReportColumn.HOST_PAYOUT]: z.number().nullable(),
  [ReportColumn.PROMOTER_PAYOUT]: z.number().nullable().optional(),
  [ReportColumn.CREDIT_CARD]: z.number().nullable(),
  [ReportColumn.STRIPE_FX_FEE]: z.number().nullable().optional(),
  [ReportColumn.WAY_FEE]: z.number().nullable().optional(),
  [ReportColumn.TRANSACTION_ROLE]: z.nativeEnum(ReportTransactionRole).nullable().optional(),
  [ReportColumn.PARTNER_NAME]: z.string().nullable().optional(),
  [ReportColumn.PARTNER_PAYOUT]: z.number().nullable().optional(),
  [ReportColumn.NET_DEPOSITED]: z.number(),
  [ReportColumn.QUANTITY]: z.number().nullable(),
  [ReportColumn.DISCOUNT_CODE]: z.string().nullable(),
  [ReportColumn.DATE_DEPOSITED]: DateStringSchema.nullable(),
  [ReportColumn.NOTES]: z.string().nullable(),
  [ReportColumn.INTERNAL_NOTES]: z.string().nullable(),
  [ReportColumn.RESERVATION_ID]: z.string().nullable(),
  [ReportColumn.CART_ID]: z.string().nullable(),
  [ReportColumn.CREATED_BY]: z.string().nullable(),
  [ReportColumn.CATEGORY]: z.string().nullable(),
  [ReportColumn.LISTING_STATUS]: z.string().nullable(),
  [ReportColumn.BOOKING_URL]: z.string().nullable(),
  [ReportColumn.PROMOTER_SOURCE]: z.string().nullable(),
  [ReportColumn.APPROVED_PROMOTERS]: z.string().nullable(),
} satisfies Record<FinancialReportColumnType | 'brandId' | 'experienceId' | 'bookingId' | 'bookingSourceUrl' | 'loyaltyMemberId' | 'loyaltyPoints' | 'externalAnalyticsUserId' | 'loyaltyTransactionId', ZodType>);

export type WebhookFinancialReportV1Item = z.infer<typeof webhookFinancialReportV1ItemSchema>;

export const financialReportItemSchema = z
  .object({
    [ReportColumn.EXTERNAL_REFERENCE_ID]: z.string().nullable(),
    [ReportColumn.BRAND_NAME]: z.string(),
    [ReportColumn.CURRENCY]: z.string(),
    [ReportColumn.EXPERIENCE_DATE]: z.union([z.date(), z.string()]),
    [ReportColumn.TRANSACTION_DATE]: z.union([z.date(), z.string()]),
    [ReportColumn.TRANSACTION_MONTH]: z.string(),
    [ReportColumn.FIRST_NAME]: z.string().nullable(),
    [ReportColumn.LAST_NAME]: z.string().nullable(),
    [ReportColumn.CONFIRMATION_CODE]: z.string().nullable(),
    [ReportColumn.PAYMENT_TYPE]: z.string(),
    [ReportColumn.PAYMENT_METHOD]: z.string(),
    [ReportColumn.ACCOUNT]: z.string().nullable().optional(),
    [ReportColumn.DESCRIPTION]: z.string(),
    [ReportColumn.DURATION]: z.string(),
    [ReportColumn.HOST]: z.string().nullable(),
    [ReportColumn.TOTAL]: z.number().nullable(),
    [ReportColumn.DISCOUNT_AMOUNT]: z.number().nullable(),
    [ReportColumn.SUBTOTAL]: z.number().nullable(),
    [ReportColumn.GROSS_REVENUE]: z.number().nullable(),
    [ReportColumn.NET_REVENUE]: z.number().nullable(),
    [ReportColumn.TAXES]: z.record(z.number().nullable()),
    [ReportColumn.TOTAL_PAID]: z.number().nullable(),
    [ReportColumn.FX_ADJUSTMENT]: z.number().nullable().optional(),
    [ReportColumn.HOST_PAYOUT]: z.number().nullable(),
    [ReportColumn.PROMOTER_PAYOUT]: z.number().nullable().optional(),
    [ReportColumn.CREDIT_CARD]: z.number().nullable(),
    [ReportColumn.STRIPE_FX_FEE]: z.number().nullable().optional(),
    [ReportColumn.WAY_FEE]: z.number().nullable().optional(),
    [ReportColumn.TRANSACTION_ROLE]: z.nativeEnum(ReportTransactionRole).nullable().optional(),
    [ReportColumn.PARTNER_NAME]: z.string().nullable().optional(),
    [ReportColumn.PARTNER_PAYOUT]: z.number().nullable().optional(),
    [ReportColumn.NET_DEPOSITED]: z.number().nullable(),
    [ReportColumn.QUANTITY]: z.number().nullable(),
    [ReportColumn.DISCOUNT_CODE]: z.string().nullable(),
    [ReportColumn.DATE_DEPOSITED]: z.string().nullable(),
    [ReportColumn.NOTES]: z.string().nullable(),
    [ReportColumn.INTERNAL_NOTES]: z.string().nullable(),
    [ReportColumn.RESERVATION_ID]: z.string().nullable().optional(),
    [ReportColumn.CART_ID]: z.string().nullable().optional(),
    [ReportColumn.CREATED_BY]: z.string().nullable().optional(),
    [ReportColumn.CATEGORY]: z.string().nullable().optional(),
    [ReportColumn.LISTING_STATUS]: z.string().nullable().optional(),
    [ReportColumn.BOOKING_URL]: z.string().nullable().optional(),
    [ReportColumn.PROMOTER_SOURCE]: z.string().nullable().optional(),
    [ReportColumn.APPROVED_PROMOTERS]: z.string().nullable().optional(),
    brandId: z.string(),
    experienceId: z.string(),
    bookingId: z.string().nullable(),
    bookingSourceUrl: z.string().nullable(),
    loyaltyMemberId: z.string().nullable().optional(),
    loyaltyPoints: z.number().nullable().optional(),
    externalAnalyticsUserId: z.string().nullable().optional(),
    loyaltyTransactionId: z.string().nullable().optional(),
  })
  .passthrough();

export type FinancialReportItem = z.infer<typeof financialReportItemSchema>;

export const PreviewFinancialEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof PreviewFinancialRequestSchema>; brandId: string }) =>
    `v1/brands/${brandId}/reports/financials/v1?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(financialReportItemSchema),
  requestSchema: z.void(),
  querySchema: PreviewFinancialRequestSchema,
});
